import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { INSIGHTS } from "@/data/insights";

const GOLD = "#C5A24A";
const CONTACT_HREF = "mailto:iainmunro@zentaraeng.com?subject=Inspection%20enquiry";

export default function Insights() {
  return (
    <div className="min-h-screen bg-[#09090B] text-[#FAFAFA] grain">
      <header className="sticky top-0 z-40 h-16 border-b border-[#27272A] bg-[#09090B]/90 backdrop-blur flex items-center justify-between px-6 md:px-12">
        <Link to="/" className="flex items-center gap-2" data-testid="insights-home-link">
          <img src="/zentara-logo.png" alt="Zentara Integrity" className="w-8 h-8 object-contain" />
          <span className="font-display font-bold text-lg">Zentara <span className="text-[#C5A24A]">Integrity</span></span>
        </Link>
        <a href={CONTACT_HREF} className="text-sm font-semibold px-4 py-2 rounded-md text-[#09090B] hover:-translate-y-0.5 transition-transform" style={{ background: GOLD }} data-testid="insights-request">
          Request an inspection
        </a>
      </header>

      <section className="max-w-5xl mx-auto px-6 md:px-12 pt-16 pb-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors mb-8" data-testid="insights-back">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="w-4 h-4" style={{ color: GOLD }} />
          <p className="label-caps" style={{ color: GOLD }}>Insights</p>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-[1.05]">
          Field notes on inspection, deterioration &amp; risk.
        </h1>
        <p className="mt-4 max-w-2xl text-[#A1A1AA] leading-relaxed">
          Short, evidence-led reads on why concrete structures fail, what regulation does and doesn't require, and how a repeatable inspection record changes the outcome.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-24 grid md:grid-cols-2 gap-6">
        {INSIGHTS.map((a, i) => (
          <motion.div key={a.slug} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
            <Link to={`/insights/${a.slug}`} className="group block border border-[#27272A] bg-[#18181B] rounded-xl overflow-hidden hover:border-[#C5A24A]/40 transition-colors" data-testid={`insight-card-${a.slug}`}>
              <div className="h-44 overflow-hidden">
                <img src={a.hero} alt={a.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-[#71717A]">
                  <span style={{ color: GOLD }}>{a.tag}</span><span>·</span><span>{a.date}</span><span>·</span><span>{a.reading}</span>
                </div>
                <h2 className="font-display text-xl font-bold mt-2 group-hover:text-white">{a.title}</h2>
                <p className="text-sm text-[#A1A1AA] mt-2 leading-relaxed">{a.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: GOLD }}>
                  Read <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          </motion.div>
        ))}
      </section>

      <footer className="border-t border-[#27272A] py-10 px-6 md:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src="/zentara-lockup.png" alt="ZENTARA As-Built & Engineering Group Ltd" className="w-14 h-14 object-contain rounded" />
          <div className="text-sm">
            <p className="font-display font-bold text-white">Zentara Integrity</p>
            <p className="text-[#71717A]">A product of ZENTARA As-Built &amp; Engineering Group Ltd.</p>
          </div>
        </div>
        <a href="https://zentaraeng.com" target="_blank" rel="noreferrer" className="text-sm text-[#71717A] hover:text-white transition-colors">zentaraeng.com</a>
      </footer>
    </div>
  );
}
